/*
 * Purpose: Recover access when passkey or device is lost
 * Last modified: 2026-07-10 | Agent
 * Completeness: 92
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AUTH_CONFIG, authUrl } from '@/lib/auth-config';
import { authFetchErrorMessage } from '@/lib/apiBase';
import { resolveTenantsForEmail } from '@/lib/auth-tenants';
import { useAuthStore } from '@/stores/authStore';
import {
  AuthButton,
  AuthCard,
  AuthEmailContextBanner,
  AuthInput,
  AuthLabel,
} from '@/components/auth/ui/AuthUi';
import { ContextualError } from '@/components/errors/ContextualError';

export function LostKeyRecoveryPanel({ next = '/dashboard' }: { next?: string }) {
  const authSignIn = useAuthStore((s) => s.signIn);
  const [email, setEmail] = useState('');
  const [sentToEmail, setSentToEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'choose' | 'otp' | 'done'>('choose');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  function completeRecovery() {
    const tenants = resolveTenantsForEmail(email);
    authSignIn(email, {
      tenants,
      activeTenantId: tenants.length === 1 ? tenants[0].id : undefined,
    });
  }

  async function sendRecoveryOtp() {
    setBusy(true);
    setError('');
    try {
      if (AUTH_CONFIG.mock) {
        setSentToEmail(email.trim());
        setStep('otp');
        setMessage('Mock mode — use code 123456');
        return;
      }
      const res = await fetch(authUrl(AUTH_CONFIG.endpoints.emailOtpSend), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, purpose: 'lost_key_recovery' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Send failed');
      setSentToEmail(email.trim());
      setStep('otp');
      setMessage('We sent a recovery code to your email.');
    } catch (e) {
      setError(authFetchErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function verifyRecoveryOtp() {
    setBusy(true);
    setError('');
    try {
      if (AUTH_CONFIG.mock && otp === '123456') {
        completeRecovery();
        setStep('done');
        setMessage('Recovery complete. Register a new passkey from sign-in.');
        return;
      }
      const res = await fetch(authUrl(AUTH_CONFIG.endpoints.emailOtpVerify), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: otp }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Invalid code');
      completeRecovery();
      setStep('done');
      setMessage('Recovery complete. Register a new passkey from sign-in.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard>
      <h1 className="text-xl font-semibold text-[var(--auth-fg)]">Lost passkey or device?</h1>
      <p className="text-sm text-[var(--auth-muted)]">
        Recover with email — then register a new passkey. No password required.
      </p>

      {step === 'choose' && (
        <>
          <label className="block">
            <AuthLabel>Account email</AuthLabel>
            <AuthInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </label>
          <AuthButton disabled={busy || !email} onClick={sendRecoveryOtp}>
            Send recovery code
          </AuthButton>
        </>
      )}

      {step === 'otp' && (
        <>
          <AuthEmailContextBanner
            email={sentToEmail || email}
            variant="otp_sent"
            onChangeEmail={() => {
              setStep('choose');
              setSentToEmail('');
              setOtp('');
              setMessage('');
            }}
          />
          <AuthInput
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className="text-center text-lg tracking-widest"
          />
          <AuthButton disabled={busy || otp.length !== 6} onClick={verifyRecoveryOtp}>
            Verify recovery code
          </AuthButton>
        </>
      )}

      {step === 'done' && (
        <AuthButton onClick={() => { window.location.href = next; }}>Continue to app</AuthButton>
      )}

      <p className="text-xs text-[var(--auth-muted)]">
        <Link to={AUTH_CONFIG.signInPath} className="underline hover:text-[var(--auth-fg)]">
          Back to sign in
        </Link>
      </p>

      {message && (
        <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
          {message}
        </p>
      )}
      {error && (
        <ContextualError
          message={error}
          action={{ label: 'Retry', onClick: () => setError('') }}
          secondaryAction={{ label: 'Sign in', href: AUTH_CONFIG.signInPath }}
        />
      )}
    </AuthCard>
  );
}

export default LostKeyRecoveryPanel;
